import React, { useState, useEffect, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';

/**
 * LumpSumAllocator
 *
 * The month-by-month body shared by two flows:
 *   • LumpSumFeeModal — the cashier pays (or settles) a lump sum.
 *   • MarkFeeModal    — an edit left money over-collected and the user chose
 *                       to push the excess into pending months.
 *
 * It owns the fee data for the student's outstanding months (mandatory
 * totals, optional fees, late fees), the per-month selections, the "mark
 * months exempted" side action, and the month-wise receipt table. Everything
 * the parent needs to build the request body — plus a live coverage preview
 * for the given pool — comes back through `onSnapshot`.
 */

export interface AllocatorCoverage {
  fullMonths: string[];
  partial: { month: string; paid: number; total: number } | null;
  leftover: number;
}

export interface AllocatorSnapshot {
  loading: boolean;
  /** Months that would be charged, chronological. */
  chargeableMonths: string[];
  /** Only chargeable months with at least one optional fee ticked. */
  optionalFeeSelections: Record<string, string[]>;
  includeLateFee: boolean;
  /** Every applicable late fee, whether or not it's being charged. */
  monthlyLateFees: Record<string, number>;
  monthlyLateDays: Record<string, number>;
  mandatoryTotal: number;
  optionalTotal: number;
  lateTotal: number;
  estimatedTotal: number;
  /** Chronological allocation of `pool`; null when pool is null/invalid. */
  coverage: AllocatorCoverage | null;
}

interface BulkOptionalFee {
  fee_type_id: string;
  fee_name: string;
  byMonth: Record<string, number>; // month_year → amount
}

interface LateFeeRaw {
  due_date: string;
  late_fee_type: 'per_day' | 'per_month' | 'percentage' | 'none';
  late_fee_amount: number;
  late_fee_max: number | null;
  grace_days: number;
}

interface LumpSumAllocatorProps {
  studentId: string | null;
  sessionId: string | null;
  session?: string | null;
  sessionStart?: string | null;
  sessionEnd?: string | null;
  classId?: string | null;
  schoolId?: string;
  /** Amount to preview allocation for. Pass null to hide the coverage preview (e.g. settlement). */
  pool: number | null;
  /** Months that must never be offered (e.g. the record being edited). */
  excludeMonths?: string[];
  /** Disables all toggles — set while the parent is submitting. */
  disabled?: boolean;
  /** Tighter paddings for embedding inside another panel. */
  compact?: boolean;
  /** Heading for the receipt table card. */
  tableTitle?: string;
  toast: React.RefObject<Toast | null>;
  onSnapshot: (s: AllocatorSnapshot) => void;
  /** Fired after months were marked exempted or a partial record was deleted, so the parent can refetch. */
  onDataChanged?: () => void;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const fmtMoney = (n: number) =>
  `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export const fmtMonthFull = (my: string) => {
  const [year, month] = my.split('-');
  const idx = parseInt(month, 10) - 1;
  return `${MONTH_NAMES[idx] ?? month} ${year}`;
};

const toId = (v: any): string => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (v.$oid) return v.$oid;
  return String(v);
};

/** "2025-26" | "2025-2026" → { startYear, endYear } */
const parseSessionYears = (session: string): { startYear: number; endYear: number } | null => {
  if (!session) return null;
  const parts = session.split('-');
  if (parts.length !== 2) return null;
  const start = parseInt(parts[0], 10);
  let end = parseInt(parts[1], 10);
  if (isNaN(start) || isNaN(end)) return null;
  if (end < 100) end = Math.floor(start / 100) * 100 + end;
  return { startYear: start, endYear: end };
};

/** Full chronological academic month list, e.g. April 2025 .. March 2026 */
export const buildAcademicMonths = (
  session: string,
  startMonth: string,
  endMonth: string
): string[] => {
  const years = parseSessionYears(session);
  if (!years) return [];
  const { startYear: yearA, endYear: yearB } = years;
  const startIdx = MONTH_NAMES.findIndex(m => m.toLowerCase() === startMonth.toLowerCase());
  const endIdx = MONTH_NAMES.findIndex(m => m.toLowerCase() === endMonth.toLowerCase());
  if (startIdx === -1 || endIdx === -1) return [];

  const months: string[] = [];
  let i = startIdx;
  let wrapped = false;
  while (true) {
    const year = wrapped ? yearB : yearA;
    months.push(`${year}-${String(i + 1).padStart(2, '0')}`);
    if (i === endIdx) break;
    i = (i + 1) % 12;
    if (i === 0) wrapped = true;
    if (months.length > 24) break;
  }
  return months;
};

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const daysBetween = (from: Date, to: Date) => {
  const ms = startOfDay(to).getTime() - startOfDay(from).getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};

const computeLateFee = (cfg: LateFeeRaw, baseForPercent: number, today = new Date()) => {
  const due = new Date(cfg.due_date);
  const grace = cfg.grace_days ?? 0;
  const effectiveDue = new Date(due);
  effectiveDue.setDate(effectiveDue.getDate() + grace);

  const isPastDue = startOfDay(today) > startOfDay(effectiveDue);
  const daysLate = isPastDue ? daysBetween(effectiveDue, today) : 0;

  let calculated = 0;
  const type = cfg.late_fee_type || 'none';
  const amount = Number(cfg.late_fee_amount) || 0;
  const max = cfg.late_fee_max != null ? Number(cfg.late_fee_max) : null;

  if (isPastDue && type !== 'none') {
    if (type === 'per_day') calculated = amount * daysLate;
    else if (type === 'per_month') calculated = amount;
    else if (type === 'percentage') calculated = (baseForPercent * amount) / 100;
    if (max != null && max > 0 && calculated > max) calculated = max;
  }

  return {
    days_late: daysLate,
    calculated_amount: Math.round(calculated * 100) / 100,
    is_late: isPastDue && calculated > 0,
  };
};

export const ALLOCATOR_CSS = `
  .lump-sum-chip {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 12px; border-radius: 10px; cursor: pointer;
    font-size: 12px; font-weight: 600; border: 1px solid #e2e8f0;
    background: white; transition: all 0.15s;
  }
  .lump-sum-chip.active {
    border-color: #4169E1; color: #4169E1;
    background: linear-gradient(135deg, rgba(65,105,225,0.1), rgba(91,124,230,0.08));
  }
  .lump-sum-chip.exempt-active {
    border-color: #f97316; color: #c2410c;
    background: rgba(249,115,22,0.08);
  }
  .lump-sum-chip.mandatory {
    border-color: #93c5fd; color: #1e40af;
    background: rgba(59,130,246,0.06);
    cursor: default;
  }
  .lump-sum-chip.mandatory-partial {
    border-color: #7dd3fc; color: #0369a1;
    background: rgba(14,165,233,0.08);
    cursor: pointer;
  }
  .lump-sum-chip.late {
    border-color: #fecaca; color: #b91c1c;
    background: rgba(239,68,68,0.06);
    cursor: default;
  }
  .lump-sum-chip.late.waived {
    border-style: dashed; border-color: #e2e8f0;
    color: #94a3b8; background: rgba(248,250,252,0.9);
    text-decoration: line-through;
  }
  .lump-sum-chip.settle {
    border-color: #a7f3d0; color: #047857;
    background: rgba(16,185,129,0.08);
    cursor: default;
  }
  .lump-sum-chip:disabled { opacity: 0.6; cursor: not-allowed; }
`;

// Module-scope so it keeps a stable component identity across renders —
// an inline definition would remount every child on each render.
const AllocCard = ({ children, compact }: { children: React.ReactNode; compact: boolean }) => (
  <div
    style={{
      background: 'rgba(255,255,255,0.85)',
      borderRadius: compact ? 12 : 16,
      padding: compact ? 14 : 20,
      border: '1px solid rgba(59,130,246,0.1)',
      boxShadow: compact ? 'none' : '0 4px 16px rgba(0,0,0,0.05)',
    }}
  >
    {children}
  </div>
);

const LumpSumAllocator: React.FC<LumpSumAllocatorProps> = ({
  studentId,
  sessionId,
  session,
  sessionStart,
  sessionEnd,
  classId,
  schoolId,
  pool,
  excludeMonths,
  disabled = false,
  compact = false,
  tableTitle = 'Month-wise Receipt',
  toast,
  onSnapshot,
  onDataChanged,
}) => {
  const [fetching, setFetching] = useState(false);
  const [eligibleMonths, setEligibleMonths] = useState<string[]>([]);
  const [recordIdByMonth, setRecordIdByMonth] = useState<Record<string, string>>({});
  const [paidAmountByMonth, setPaidAmountByMonth] = useState<Record<string, number>>({});
  const [recordTotalByMonth, setRecordTotalByMonth] = useState<Record<string, number>>({});
  const [confirmDeleteMonth, setConfirmDeleteMonth] = useState<string | null>(null);
  const [deletingMonth, setDeletingMonth] = useState<string | null>(null);
  const [mandatoryByMonth, setMandatoryByMonth] = useState<Record<string, number>>({});

  const [bulkOptionalFees, setBulkOptionalFees] = useState<BulkOptionalFee[]>([]);
  const [optionalSelections, setOptionalSelections] = useState<Record<string, string[]>>({});

  const [includeLateFee, setIncludeLateFee] = useState(false);
  const [bulkLateByMonth, setBulkLateByMonth] = useState<Record<string, number>>({});
  const [bulkLateDaysByMonth, setBulkLateDaysByMonth] = useState<Record<string, number>>({});

  const [exemptSelection, setExemptSelection] = useState<string[]>([]);
  const [exemptionReason, setExemptionReason] = useState('');
  const [exempting, setExempting] = useState(false);

  const excludeKey = (excludeMonths ?? []).join(',');

  useEffect(() => {
    if (studentId && sessionId && session && sessionStart && sessionEnd) {
      loadBreakdown();
    } else {
      setEligibleMonths([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, sessionId, session, sessionStart, sessionEnd, classId, excludeKey]);

  const loadBreakdown = async () => {
    if (!studentId || !sessionId || !session || !sessionStart || !sessionEnd) return;
    setFetching(true);
    try {
      const allMonths = buildAcademicMonths(session, sessionStart, sessionEnd);
      if (allMonths.length === 0) {
        setEligibleMonths([]);
        return;
      }
      const excluded = new Set(excludeMonths ?? []);

      // Which months already have a fee record and what status they're in
      const monthlyParams = new URLSearchParams({
        student_id: studentId,
        session_id: sessionId,
        start_month_year: allMonths[0],
        end_month_year: allMonths[allMonths.length - 1],
      });
      const monthlyRes = await apiClient.get(`/api/fee-records/monthly?${monthlyParams.toString()}`);

      let eligible: string[] = allMonths.filter(my => !excluded.has(my));
      const idMap: Record<string, string> = {};
      const paidMap: Record<string, number> = {};
      const totalMap: Record<string, number> = {};
      if (monthlyRes.data.status === 'success') {
        const statusMap: Record<string, string> = {};
        const settledMap: Record<string, boolean> = {};
        for (const row of monthlyRes.data.data || []) {
          statusMap[row.month_year] = row.fees_status;
          settledMap[row.month_year] = !!row.is_settled;
          if (row.fees_status === 'partially_paid' && row.fee_record_id) {
            idMap[row.month_year] = String(
              typeof row.fee_record_id === 'object' ? row.fee_record_id.$oid || row.fee_record_id : row.fee_record_id
            );
            paidMap[row.month_year] = Number(row.paid_amount) || 0;
            totalMap[row.month_year] = Number(row.total_amount) || 0;
          }
        }
        eligible = eligible.filter(my => {
          if (settledMap[my]) return false; // closed by agreement — never reopen
          const status = statusMap[my] || 'unpaid';
          return status === 'unpaid' || status === 'partially_paid';
        });
      }
      setEligibleMonths(eligible);
      setRecordIdByMonth(idMap);
      setPaidAmountByMonth(paidMap);
      setRecordTotalByMonth(totalMap);

      if (eligible.length === 0) {
        setBulkOptionalFees([]);
        setBulkLateByMonth({});
        setBulkLateDaysByMonth({});
        setMandatoryByMonth({});
        return;
      }

      // Optional fees available per eligible month
      const optFeeMap = new Map<string, BulkOptionalFee>();
      for (const my of eligible) {
        try {
          const optParams = new URLSearchParams({ student_id: studentId, session_id: sessionId, month_year: my });
          const optRes = await apiClient.get(`/api/fee-records/optional-active?${optParams.toString()}`);
          if (optRes.data.status === 'success') {
            for (const f of optRes.data.data || []) {
              const id = toId(f.fee_type_id);
              if (!id || f.amount == null) continue;
              if (!optFeeMap.has(id)) {
                optFeeMap.set(id, { fee_type_id: id, fee_name: f.fee_name || '—', byMonth: {} });
              }
              optFeeMap.get(id)!.byMonth[my] = Number(f.amount) || 0;
            }
          }
        } catch {
          // best-effort
        }
      }
      setBulkOptionalFees(Array.from(optFeeMap.values()));

      // Mandatory total per eligible month — structures can vary by month
      const mandatoryMap: Record<string, number> = {};
      if (classId) {
        const optIds = new Set(optFeeMap.keys());
        for (const my of eligible) {
          try {
            const structParams = new URLSearchParams({
              session_id: sessionId,
              class_id: classId,
              month_year: my,
              limit: '100',
            });
            if (schoolId) structParams.set('school_id', schoolId);
            const structRes = await apiClient.get(`/api/fee-structures?${structParams.toString()}`);
            if (structRes.data.status === 'success') {
              let monthTotal = 0;
              for (const s of structRes.data.data || []) {
                const id = toId(s.fee_type_id);
                if (!id || optIds.has(id)) continue;
                monthTotal += Number(s.amount) || 0;
              }
              mandatoryMap[my] = monthTotal;
            }
          } catch {
            // best-effort
          }
        }
      }
      setMandatoryByMonth(mandatoryMap);

      // Late fee config per eligible month
      if (classId) {
        try {
          const lateParams = new URLSearchParams({ session_id: sessionId, class_id: classId, limit: '50' });
          if (schoolId) lateParams.set('school_id', schoolId);
          const lateRes = await apiClient.get(`/api/fee-late-config?${lateParams.toString()}`);
          if (lateRes.data.status === 'success') {
            const monthEntries: Record<string, any> = {};
            for (const cfg of lateRes.data.data || []) {
              for (const m of cfg.months || []) {
                const my = m.month_year || m.MonthYear;
                if (my) monthEntries[my] = m;
              }
            }
            const lateMap: Record<string, number> = {};
            const daysMap: Record<string, number> = {};
            for (const my of eligible) {
              const entry = monthEntries[my];
              if (!entry) continue;
              const raw: LateFeeRaw = {
                due_date: entry.due_date || entry.DueDate,
                late_fee_type: (entry.late_fee_type || entry.LateFeeType || 'none') as LateFeeRaw['late_fee_type'],
                late_fee_amount: Number(entry.late_fee_amount ?? entry.LateFeeAmount ?? 0),
                late_fee_max:
                  entry.late_fee_max != null || entry.LateFeeMax != null
                    ? Number(entry.late_fee_max ?? entry.LateFeeMax)
                    : null,
                grace_days: Number(entry.grace_days ?? entry.GraceDays ?? 0),
              };
              const info = computeLateFee(raw, mandatoryMap[my] || 0);
              if (info.is_late) {
                lateMap[my] = info.calculated_amount;
                daysMap[my] = info.days_late;
              }
            }
            setBulkLateByMonth(lateMap);
            setBulkLateDaysByMonth(daysMap);
          }
        } catch {
          // best-effort
        }
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to load fee details',
        life: 4000,
      });
    } finally {
      setFetching(false);
    }
  };

  // ── Selection helpers ──
  const toggleOptionalForMonth = (my: string, feeTypeId: string) => {
    setOptionalSelections(prev => {
      const current = prev[my] || [];
      const next = current.includes(feeTypeId)
        ? current.filter(id => id !== feeTypeId)
        : [...current, feeTypeId];
      return { ...prev, [my]: next };
    });
  };

  const toggleExemptMonth = (my: string) => {
    setExemptSelection(prev => (prev.includes(my) ? prev.filter(m => m !== my) : [...prev, my]));
  };

  const allEligibleExempted = eligibleMonths.length > 0 && eligibleMonths.every(my => exemptSelection.includes(my));

  const toggleSelectAllExempt = () => {
    setExemptSelection(allEligibleExempted ? [] : [...eligibleMonths]);
  };

  // Months still shown as chargeable — pending-exemption months drop out of
  // the estimate while the user is ticking boxes, but nothing is committed
  // until "Mark Exempted" is pressed.
  const chargeableMonths = useMemo(
    () => eligibleMonths.filter(my => !exemptSelection.includes(my)),
    [eligibleMonths, exemptSelection]
  );

  const optionalSelectableMonths = useMemo(
    () => chargeableMonths.filter(my => bulkOptionalFees.some(f => f.byMonth[my] != null)),
    [chargeableMonths, bulkOptionalFees]
  );

  const allOptionalSelected =
    optionalSelectableMonths.length > 0 &&
    optionalSelectableMonths.every(my => {
      const feesForMonth = bulkOptionalFees.filter(f => f.byMonth[my] != null);
      return feesForMonth.every(f => (optionalSelections[my] || []).includes(f.fee_type_id));
    });

  const toggleSelectAllOptional = () => {
    const shouldSelectAll = !allOptionalSelected;
    setOptionalSelections(prev => {
      const next = { ...prev };
      optionalSelectableMonths.forEach(my => {
        const feesForMonth = bulkOptionalFees.filter(f => f.byMonth[my] != null);
        next[my] = shouldSelectAll ? feesForMonth.map(f => f.fee_type_id) : [];
      });
      return next;
    });
  };

  const lateMonths = useMemo(
    () => chargeableMonths.filter(my => (bulkLateByMonth[my] || 0) > 0),
    [chargeableMonths, bulkLateByMonth]
  );
  const hasLate = lateMonths.length > 0;

  const lateApplicableTotal = useMemo(
    () => lateMonths.reduce((s, my) => s + (bulkLateByMonth[my] || 0), 0),
    [lateMonths, bulkLateByMonth]
  );

  const optionalPreviewTotal = useMemo(
    () =>
      chargeableMonths.reduce((sum, my) => {
        const selected = optionalSelections[my] || [];
        return (
          sum +
          selected.reduce((s, id) => {
            const fee = bulkOptionalFees.find(f => f.fee_type_id === id);
            return s + (fee?.byMonth[my] || 0);
          }, 0)
        );
      }, 0),
    [chargeableMonths, optionalSelections, bulkOptionalFees]
  );

  const latePreviewTotal = includeLateFee ? lateApplicableTotal : 0;

  const mandatoryPreviewTotal = useMemo(
    () => chargeableMonths.reduce((s, my) => s + (mandatoryByMonth[my] || 0), 0),
    [chargeableMonths, mandatoryByMonth]
  );

  const estimatedTotal = mandatoryPreviewTotal + optionalPreviewTotal + latePreviewTotal;

  // Live preview mirroring the backend's chronological allocation
  const coverage = useMemo<AllocatorCoverage | null>(() => {
    if (pool == null || isNaN(pool) || pool <= 0) return null;

    const monthTotal = (my: string) => {
      const mandatory = mandatoryByMonth[my] || 0;
      const optional = (optionalSelections[my] || []).reduce((s, id) => {
        const fee = bulkOptionalFees.find(f => f.fee_type_id === id);
        return s + (fee?.byMonth[my] || 0);
      }, 0);
      const late = includeLateFee ? bulkLateByMonth[my] || 0 : 0;
      return mandatory + optional + late;
    };

    let remaining = pool;
    const fullMonths: string[] = [];
    let partial: AllocatorCoverage['partial'] = null;

    for (const my of chargeableMonths) {
      if (remaining <= 0) break;
      const total = monthTotal(my);
      if (total <= 0) continue;
      if (remaining >= total) {
        fullMonths.push(my);
        remaining -= total;
      } else {
        partial = { month: my, paid: remaining, total };
        remaining = 0;
      }
    }

    return { fullMonths, partial, leftover: Math.round(Math.max(0, remaining) * 100) / 100 };
  }, [pool, chargeableMonths, mandatoryByMonth, optionalSelections, bulkOptionalFees, includeLateFee, bulkLateByMonth]);

  // ── Report everything the parent needs to submit ──
  useEffect(() => {
    const nonEmpty: Record<string, string[]> = {};
    for (const my of chargeableMonths) {
      const ids = optionalSelections[my] || [];
      if (ids.length > 0) nonEmpty[my] = ids;
    }
    onSnapshot({
      loading: fetching,
      chargeableMonths,
      optionalFeeSelections: nonEmpty,
      includeLateFee,
      monthlyLateFees: bulkLateByMonth,
      monthlyLateDays: bulkLateDaysByMonth,
      mandatoryTotal: mandatoryPreviewTotal,
      optionalTotal: optionalPreviewTotal,
      lateTotal: latePreviewTotal,
      estimatedTotal,
      coverage,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetching,
    chargeableMonths,
    optionalSelections,
    includeLateFee,
    bulkLateByMonth,
    bulkLateDaysByMonth,
    mandatoryPreviewTotal,
    optionalPreviewTotal,
    latePreviewTotal,
    estimatedTotal,
    coverage,
  ]);

  // ── Side actions: delete a partial record, mark months exempted ──
  const handleDeleteRecord = async (my: string) => {
    const recordId = recordIdByMonth[my];
    if (!recordId) return;
    setDeletingMonth(my);
    try {
      const res = await apiClient.delete(`/api/fee-records/${recordId}`);
      if (res.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: res.data.message || 'Fee record deleted successfully.',
          life: 3000,
        });
        setConfirmDeleteMonth(null);
        onDataChanged?.();
        loadBreakdown();
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.message || 'Failed to delete fee record', life: 5000 });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to delete fee record',
        life: 5000,
      });
    } finally {
      setDeletingMonth(null);
    }
  };

  const handleMarkExempted = async () => {
    if (!studentId || !sessionId || !session || exemptSelection.length === 0) return;
    setExempting(true);
    let ok = 0;
    let fail = 0;
    for (const my of exemptSelection) {
      try {
        const body: Record<string, any> = {
          session_id: sessionId,
          session,
          student_id: studentId,
          month_year: my,
          pay_mode: 'cash',
          is_exempted: true,
        };
        if (schoolId) body.school_id = schoolId;
        if (exemptionReason.trim()) body.exemption_reason = exemptionReason.trim();
        const res = await apiClient.post('/api/fee-records', body);
        if (res.data.status === 'success') ok++;
        else fail++;
      } catch {
        fail++;
      }
    }
    setExempting(false);
    if (ok > 0) {
      toast.current?.show({
        severity: fail ? 'warn' : 'success',
        summary: fail ? 'Partial success' : 'Success',
        detail: `${ok} month(s) marked exempted${fail ? `, ${fail} failed` : ''}.`,
        life: 4000,
      });
      setExemptSelection([]);
      setExemptionReason('');
      onDataChanged?.();
      loadBreakdown();
    } else {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not exempt any months', life: 5000 });
    }
  };

  const busy = disabled || exempting;

  if (fetching) {
    return (
      <>
        <style>{ALLOCATOR_CSS}</style>
        <AllocCard compact={compact}>
          <span style={{ fontSize: 13, color: '#64748b' }}>Loading fee details...</span>
        </AllocCard>
      </>
    );
  }

  const activeMandatoryMonths = chargeableMonths;

  return (
    <>
      <style>{ALLOCATOR_CSS}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 10 : 16 }}>
        {eligibleMonths.length === 0 && (
          <AllocCard compact={compact}>
            <span style={{ fontSize: 13, color: '#64748b' }}>
              No pending months — every month in this session is already paid, exempted or settled.
            </span>
          </AllocCard>
        )}

        {/* ── Mandatory fee breakdown per month ── */}
        {activeMandatoryMonths.length > 0 && (
          <AllocCard compact={compact}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a8a', marginBottom: 10 }}>
              Monthly Mandatory Fees
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {activeMandatoryMonths.map(my => {
                const recordId = recordIdByMonth[my];
                const amountLabel = recordId
                  ? `${fmtMoney(paidAmountByMonth[my] || 0)} / ${fmtMoney(recordTotalByMonth[my] || 0)}`
                  : mandatoryByMonth[my] != null
                    ? fmtMoney(mandatoryByMonth[my])
                    : '—';
                return (
                  <button
                    key={my}
                    type="button"
                    className={`lump-sum-chip mandatory ${recordId ? 'mandatory-partial' : ''}`}
                    onClick={() => recordId && !busy && setConfirmDeleteMonth(my)}
                    title={recordId ? 'Click to delete this partial payment record' : undefined}
                  >
                    {fmtMonthFull(my)} · {amountLabel}
                    {recordId && <i className="pi pi-trash" style={{ fontSize: 11 }} />}
                  </button>
                );
              })}
            </div>
            {confirmDeleteMonth && activeMandatoryMonths.includes(confirmDeleteMonth) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginTop: 12,
                  padding: '8px 12px',
                  borderRadius: 10,
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                }}
              >
                <span style={{ fontSize: 11, color: '#991b1b', fontWeight: 600, flex: 1 }}>
                  Delete the partial payment record for {fmtMonthFull(confirmDeleteMonth)}? This can't be undone.
                </span>
                <button
                  type="button"
                  onClick={() => handleDeleteRecord(confirmDeleteMonth)}
                  disabled={deletingMonth === confirmDeleteMonth}
                  style={{
                    fontSize: 11, fontWeight: 700, color: 'white', background: '#ef4444',
                    border: 'none', borderRadius: 6, padding: '4px 10px',
                    cursor: deletingMonth === confirmDeleteMonth ? 'not-allowed' : 'pointer',
                  }}
                >
                  {deletingMonth === confirmDeleteMonth ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDeleteMonth(null)}
                  disabled={deletingMonth === confirmDeleteMonth}
                  style={{
                    fontSize: 11, fontWeight: 700, color: '#64748b', background: 'white',
                    border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 10px',
                    cursor: deletingMonth === confirmDeleteMonth ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </AllocCard>
        )}

        {/* ── Optional fees, selected per month ── */}
        {bulkOptionalFees.length > 0 && chargeableMonths.length > 0 && (
          <AllocCard compact={compact}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a8a' }}>Include Optional Fees</div>
              {optionalSelectableMonths.length > 1 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={allOptionalSelected} onChange={toggleSelectAllOptional} disabled={busy} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#1e3a8a' }}>Select All</span>
                </label>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 12, lineHeight: 1.4 }}>
              Pick which optional fees apply to which month — a fee only shows for months it's configured for.
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {chargeableMonths.map(my => {
                const feesForMonth = bulkOptionalFees.filter(f => f.byMonth[my] != null);
                if (feesForMonth.length === 0) return null;
                return (
                  <div
                    key={my}
                    style={{
                      flex: '0 1 auto',
                      minWidth: 150,
                      padding: '8px 10px',
                      borderRadius: 10,
                      background: 'rgba(248,250,252,0.9)',
                      border: '1px solid rgba(226,232,240,0.8)',
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                      {fmtMonthFull(my)}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {feesForMonth.map(fee => {
                        const active = (optionalSelections[my] || []).includes(fee.fee_type_id);
                        return (
                          <button
                            key={fee.fee_type_id}
                            type="button"
                            className={`lump-sum-chip ${active ? 'active' : ''}`}
                            onClick={() => toggleOptionalForMonth(my, fee.fee_type_id)}
                            disabled={busy}
                          >
                            {fee.fee_name} · {fmtMoney(fee.byMonth[my])}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </AllocCard>
        )}

        {/* ── Mark months exempted (separate from paying the pool) ── */}
        {eligibleMonths.length > 0 && (
          <AllocCard compact={compact}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#c2410c' }}>Mark Months Exempted (optional)</div>
              {eligibleMonths.length > 1 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={allEligibleExempted} onChange={toggleSelectAllExempt} disabled={busy} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#c2410c' }}>Select All</span>
                </label>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: exemptSelection.length > 0 ? 12 : 0 }}>
              {eligibleMonths.map(my => (
                <button
                  key={my}
                  type="button"
                  className={`lump-sum-chip ${exemptSelection.includes(my) ? 'exempt-active' : ''}`}
                  onClick={() => toggleExemptMonth(my)}
                  disabled={busy}
                >
                  {fmtMonthFull(my)}
                </button>
              ))}
            </div>
            {exemptSelection.length > 0 && (
              <>
                <div style={{ marginBottom: 10 }}>
                  <GlobalInputField
                    value={exemptionReason}
                    onChange={e => setExemptionReason(e.target.value)}
                    placeholder="Exemption reason (optional)"
                    disabled={busy}
                  />
                </div>
                <GlobalButton
                  label={exempting ? 'Marking...' : `Mark ${exemptSelection.length} Month(s) Exempted`}
                  onClick={handleMarkExempted}
                  variant="secondary"
                  size="small"
                  disabled={busy}
                  loading={exempting}
                  fullWidth
                />
              </>
            )}
          </AllocCard>
        )}

        {/* ── Pending exemption strip ── */}
        {exemptSelection.length > 0 && (
          <AllocCard compact={compact}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#c2410c', marginBottom: 10 }}>Pending Exemption</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {exemptSelection.map(my => (
                <span key={my} className="lump-sum-chip exempt-active" style={{ cursor: 'default', opacity: 0.85 }}>
                  {fmtMonthFull(my)}
                </span>
              ))}
            </div>
          </AllocCard>
        )}

        {/* ── Late fee — one informational chip per overdue month ── */}
        {hasLate && (
          <AllocCard compact={compact}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#b91c1c' }}>
                Late Fees — {lateMonths.length} overdue month{lateMonths.length > 1 ? 's' : ''}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={includeLateFee}
                  onChange={e => setIncludeLateFee(e.target.checked)}
                  disabled={busy}
                />
                <span style={{ fontSize: 11, fontWeight: 600, color: '#b91c1c' }}>Charge late fees</span>
              </label>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {lateMonths.map(my => (
                <span key={my} className={`lump-sum-chip late ${includeLateFee ? '' : 'waived'}`}>
                  {fmtMonthFull(my)} · {fmtMoney(bulkLateByMonth[my])}
                  {bulkLateDaysByMonth[my] ? ` · ${bulkLateDaysByMonth[my]}d late` : ''}
                </span>
              ))}
            </div>
            <div style={{ fontSize: 11, color: includeLateFee ? '#b91c1c' : '#64748b', marginTop: 10, lineHeight: 1.4 }}>
              {includeLateFee
                ? `${fmtMoney(lateApplicableTotal)} added to the outstanding total across these months.`
                : `Late fees waived. Each month is saved with late_fee_exempted and the waived amount (${fmtMoney(
                    lateApplicableTotal
                  )} in total) so the waiver stays on record.`}
            </div>
          </AllocCard>
        )}

        {/* ── Month-wise receipt ── */}
        {chargeableMonths.length > 0 && (
          <AllocCard compact={compact}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a8a', marginBottom: 4 }}>{tableTitle}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10, lineHeight: 1.4 }}>
              Exactly what's charged for each month — mandatory applies to every chargeable month, optional fees
              only where you've selected them.
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #1e3a8a' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: '#1e3a8a', fontWeight: 700 }}>Month</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', color: '#1e3a8a', fontWeight: 700 }}>Mandatory</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', color: '#1e3a8a', fontWeight: 700 }}>Optional</th>
                    {hasLate && (
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: '#1e3a8a', fontWeight: 700 }}>Late Fee</th>
                    )}
                    <th style={{ textAlign: 'right', padding: '6px 8px', color: '#1e3a8a', fontWeight: 700 }}>Row Total</th>
                    {coverage && (
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: '#1e3a8a', fontWeight: 700 }}>Covered</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {chargeableMonths.map(my => {
                    const mandatory = mandatoryByMonth[my] || 0;
                    const selectedIds = optionalSelections[my] || [];
                    const optionalNames = selectedIds
                      .map(id => bulkOptionalFees.find(f => f.fee_type_id === id)?.fee_name)
                      .filter(Boolean) as string[];
                    const optional = selectedIds.reduce((s, id) => {
                      const fee = bulkOptionalFees.find(f => f.fee_type_id === id);
                      return s + (fee?.byMonth[my] || 0);
                    }, 0);
                    const applicableLate = bulkLateByMonth[my] || 0;
                    const late = includeLateFee ? applicableLate : 0;
                    const rowTotal = mandatory + optional + late;

                    let coveredCell: React.ReactNode = null;
                    if (coverage) {
                      if (coverage.fullMonths.includes(my)) {
                        coveredCell = <span style={{ color: '#047857', fontWeight: 700 }}>{fmtMoney(rowTotal)}</span>;
                      } else if (coverage.partial?.month === my) {
                        coveredCell = <span style={{ color: '#b45309', fontWeight: 700 }}>{fmtMoney(coverage.partial.paid)}</span>;
                      } else {
                        coveredCell = <span style={{ color: '#cbd5e1' }}>—</span>;
                      }
                    }

                    return (
                      <tr key={my} style={{ borderBottom: '1px dashed #e2e8f0' }}>
                        <td style={{ padding: '6px 8px', color: '#1e293b', fontWeight: 600, verticalAlign: 'top' }}>
                          {fmtMonthFull(my)}
                          {optionalNames.length > 0 && (
                            <div style={{ fontSize: 10, color: '#0369a1', fontWeight: 500, marginTop: 2 }}>
                              + {optionalNames.join(', ')}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: '#1e293b' }}>{fmtMoney(mandatory)}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: optional > 0 ? '#0369a1' : '#cbd5e1' }}>
                          {optional > 0 ? fmtMoney(optional) : '—'}
                        </td>
                        {hasLate && (
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: late > 0 ? '#c2410c' : '#cbd5e1' }}>
                            {late > 0 ? (
                              fmtMoney(late)
                            ) : applicableLate > 0 ? (
                              <s style={{ color: '#94a3b8' }}>{fmtMoney(applicableLate)}</s>
                            ) : (
                              '—'
                            )}
                          </td>
                        )}
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#1e40af' }}>
                          {fmtMoney(rowTotal)}
                        </td>
                        {coverage && <td style={{ padding: '6px 8px', textAlign: 'right' }}>{coveredCell}</td>}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #1e3a8a' }}>
                    <td style={{ padding: '8px', fontWeight: 700, color: '#1e3a8a' }}>Total</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>{fmtMoney(mandatoryPreviewTotal)}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>{fmtMoney(optionalPreviewTotal)}</td>
                    {hasLate && (
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>
                        {includeLateFee ? fmtMoney(latePreviewTotal) : <s style={{ color: '#94a3b8' }}>{fmtMoney(lateApplicableTotal)}</s>}
                      </td>
                    )}
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 800, fontSize: 14, color: '#1e40af' }}>
                      {fmtMoney(estimatedTotal)}
                    </td>
                    {coverage && (
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: 800, color: '#047857' }}>
                        {fmtMoney(Math.min(pool ?? 0, estimatedTotal))}
                      </td>
                    )}
                  </tr>
                </tfoot>
              </table>
            </div>
          </AllocCard>
        )}
      </div>
    </>
  );
};

export default LumpSumAllocator;